import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import slice from './ui.slice';

import activeEditor from 'domains/active_editor';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectVisualRotation = (state: LocalState) => state.visualOnlyRotation;
const selectActiveSpecialContentSidebarMenu = (state: LocalState) =>
  state.activeSpecialContentSidebarMenu;

const selectShowUploadPhotosDialog = (state: LocalState) => state.showUploadPhotosDialog;
const selectSidebarComponent = (state: LocalState) => state.sidebarComponent;

const selectSiteMessage = (state: LocalState) => state.siteMessage;

const selectHideBranding = (state: LocalState) => state.hideBranding;
const selectShowFilenames = (state: LocalState) => state.showFilenames;
const selectTheme = (state: LocalState) => state.theme;

const selectShowAdvancedFiltersState = (state: LocalState) => state.showAdvancedFilters;

// To show, must be available _and_ set true in state
const selectShowAdvancedFilters = createSelector(
  [activeEditor.selectors.hasAdvancedFilters, compose(selectShowAdvancedFiltersState, localState)],
  (hasAdvancedFilters, showAdvancedFiltersState) => hasAdvancedFilters && showAdvancedFiltersState
);

// Component level error message selectors
const selectCustomSizeErrors = (state: LocalState) => state.errorMessages.customSizePicker;

// Mobile UI State
const selectHasMobileNavigation = (state: LocalState) => state.hasMobileNavigation;
const selectShowEditorSidebar = (state: LocalState) => state.showEditorSidebar;
const selectShowMobileSidebar = (state: LocalState) => state.showMobileSidebar;
const selectShowSidebarProducts = (state: LocalState) => state.showSidebarProducts;
const selectShowMobileUserPhotos = (state: LocalState) => state.showMobileUserPhotos;

export default {
  visualRotation: compose(selectVisualRotation, localState),
  activeSidebarMenu: compose(selectActiveSpecialContentSidebarMenu, localState),
  showUploadPhotosDialog: compose(selectShowUploadPhotosDialog, localState),
  sidebarComponent: compose(selectSidebarComponent, localState),
  siteMessage: compose(selectSiteMessage, localState),
  hideBranding: compose(selectHideBranding, localState),
  showFilenames: compose(selectShowFilenames, localState),
  theme: compose(selectTheme, localState),
  customSizeErrors: compose(selectCustomSizeErrors, localState),
  hasMobileNavigation: compose(selectHasMobileNavigation, localState),
  showEditorSidebar: compose(selectShowEditorSidebar, localState),
  showMobileSidebar: compose(selectShowMobileSidebar, localState),
  showSidebarProducts: compose(selectShowSidebarProducts, localState),
  showMobileUserPhotos: compose(selectShowMobileUserPhotos, localState),
  showAdvancedFilters: selectShowAdvancedFilters,
};
